import { useMemo } from 'react';
import useAsync from 'react-use/lib/useAsync';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { getAllEntities } from '../components/common/getAllEntities';
import type { DropdownOption } from '../components/common/types';

interface UseEntityOptionsResult {
  apiStringOptions: string[];
  componentDropdownOptions: DropdownOption[];
}

const toRef = (kind: string, ns: string, name: string) =>
  `${kind}:${ns}/${name}`;

export const useEntityOptions = (): UseEntityOptionsResult => {
  const catalogApi = useApi(catalogApiRef);

  const { value: apis = [] } = useAsync(async () => {
    try {
      return await getAllEntities(catalogApi, { filter: [{ kind: 'API' }] });
    } catch (e) {
      console.error('Failed to fetch APIs for relations', e);
      return [];
    }
  }, [catalogApi]);

  const { value: components = [] } = useAsync(async () => {
    try {
      return await getAllEntities(catalogApi, {
        filter: [{ kind: 'Component' }],
      });
    } catch (e) {
      console.error('Failed to fetch Components for relations', e);
      return [];
    }
  }, [catalogApi]);

  const apiStringOptions = useMemo(
    () =>
      (apis as any[]).map(e =>
        toRef(
          'api',
          e?.metadata?.namespace ?? 'default',
          e?.metadata?.name ?? '',
        ),
      ),
    [apis],
  );

  const componentDropdownOptions = useMemo(
    () =>
      (components as any[]).map(e => {
        const ns = e?.metadata?.namespace ?? 'default';
        const name = e?.metadata?.name ?? '';
        return {
          label: `${ns}/${name}`,
          value: toRef('component', ns, name),
        };
      }),
    [components],
  );

  return { apiStringOptions, componentDropdownOptions };
};
